<template>
  <div>
    <b-overlay :show="isLoading" rounded="sm">
      <b-container fluid>
        <b-navbar toggleable="lg" type="dark" variant="light">
          <b-navbar-brand href="#" class="text-dark">
            <strong>Stop Words</strong>
          </b-navbar-brand>

          <b-navbar-toggle target="nav-collapse"></b-navbar-toggle>

          <b-collapse id="nav-collapse" is-nav>
            <!-- Right aligned nav items -->
            <b-navbar-nav class="ml-auto">
              <b-nav-form>
                <b-button
                  variant="outline-primary"
                  size="sm"
                  @click="showModal = !showModal"
                  class="mx-2"
                >
                  <font-awesome-icon icon="plus" />
                </b-button>

                <b-button variant="outline-info" size="sm" @click="reloadTable">
                  <font-awesome-icon icon="sync" />
                </b-button>
              </b-nav-form>
            </b-navbar-nav>
            <b-navbar-nav class="ml-auto">
              <b-nav-form>
                <b-form-group
                  label-cols-sm="3"
                  label-align-sm="right"
                  label-size="sm"
                  label-for="filterInput"
                >
                  <b-input-group size="sm">
                    <b-form-input
                      v-model="filter"
                      type="search"
                      id="filterInput"
                      placeholder="Type to Search"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!filter" @click="filter = ''">Clear</b-button>
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </b-nav-form>
            </b-navbar-nav>
          </b-collapse>
        </b-navbar>

        <b-table
          class="mx-1"
          bordered
          sticky-header="calc(100vh - 200px)"
          ref="table"
          id="my-table"
          :busy.sync="isLoading"
          :items="getFilteredKeywordsData"
          :fields="tableFields"
          :filter="filter"
          :per-page="perPage"
          :current-page="currentPage"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          responsive="sm"
        >
          <template v-slot:cell(created_on)="data">{{ age(data.item.created_on) }}</template>
          <template v-slot:cell(actions)="data">
            <div class="text-nowrap">
              <b-button
                size="sm"
                class="mr-2"
                variant="outline-info"
                @click="onEditFilteredKeywords(data)"
              >
                <font-awesome-icon v-b-tooltip.hover title="Edit Field" icon="edit" />
              </b-button>
              <b-button size="sm" variant="outline-danger" @click="onDeleteFilteredKeywords(data)">
                <font-awesome-icon v-b-tooltip.hover title="Delete Field" icon="trash" />
              </b-button>
            </div>
          </template>
        </b-table>
        <b-row>
          <b-col cols="2">
            <b-form-select v-model="perPage" id="perPageSelect" size="sm" :options="pageOptions"></b-form-select>
          </b-col>
          <b-col>
            <b-pagination
              class="float-right"
              v-model="currentPage"
              :total-rows="rows"
              :per-page="perPage"
              first-text="First"
              prev-text="Prev"
              next-text="Next"
              last-text="Last"
              aria-controls="my-table"
            ></b-pagination>
          </b-col>
        </b-row>
      </b-container>
    </b-overlay>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      size="lg"
      :title="'Create Filtered Keywords'"
      v-model="showModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <h4>Stop Keywords</h4>
          <b-input-group class="mt-3">
            <b-form-input @keyup.enter="addStopWords()" v-model="addStopWord"></b-form-input>
            <b-input-group-append>
              <b-button @click="addStopWords" variant="outline-warning">Add</b-button>
            </b-input-group-append>
          </b-input-group>
          <b-table :items="stopWordsList" :fields="fieldsKeywords">
            <template v-slot:cell(text)="data">
              <p>{{data.item}}</p>
            </template>
            <template v-slot:cell(actions)="data">
              <b-button @click="removeStopWords(data)" variant="danger" size="sm">
                <font-awesome-icon icon="trash"></font-awesome-icon>
              </b-button>
            </template>
          </b-table>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>{{'Your Filtered Keywords is being Creating......'}}</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            {{ 'Your Filtered Keywords has been added successfully.' }}
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" variant="outline-danger" @click="resetModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="outline-success"
          @click="checkValidation()"
        >{{ 'Save' }}</b-button>
      </template>
    </b-modal>
    <b-modal
      no-close-on-backdrop
      no-close-on-esc
      hide-header-close
      size="lg"
      :title="'Update Filtered Keywords'"
      v-model="showEditModal"
    >
      <div>
        <div v-if="!submiting && !responseSuccess">
          <h4>Stop Keywords</h4>
          <b-form-group class="mt-3">
            <b-form-input v-model="addStopWord"></b-form-input>
          </b-form-group>
        </div>
        <div v-else-if="submiting && !responseSuccess">
          <p>{{ 'Your Filtered Keywords is being Updating......' }}</p>
          <b-spinner type="grow" label="Loading..."></b-spinner>
        </div>
        <div v-else>
          <p class="my-2 text-center">
            {{ 'Your Filtered Keywords has been updated successfully.' }}
            <br />
          </p>
        </div>
      </div>
      <template v-slot:modal-footer>
        <b-button v-if="!submiting" variant="outline-danger" @click="resetModal">Close</b-button>
        <b-button
          v-if="!submiting && !responseSuccess"
          variant="outline-success"
          @click="checkValidation()"
        >{{ 'Update' }}</b-button>
      </template>
    </b-modal>
    <b-overlay :show="showDeleteFilteredKeywordsModal" no-wrap>
      <template v-slot:overlay>
        <div v-if="processing" class="text-center p-4 bg-primary text-light rounded">
          <font-awesome-icon size="lg" icon="cloud-upload-alt" />
          <div class="mb-3">Deleting...</div>
          <b-progress
            min="1"
            max="20"
            :value="counter"
            variant="success"
            height="3px"
            class="mx-n4 rounded-0"
          ></b-progress>
        </div>
        <div
          v-else
          ref="dialog"
          tabindex="-1"
          role="dialog"
          aria-modal="false"
          aria-labelledby="form-confirm-label"
          class="text-center p-3"
        >
          <p>
            <strong id="form-confirm-label">Are you sure?</strong>
          </p>
          <div class="d-flex">
            <b-button variant="outline-success" class="mr-3" @click="resetDeleteModal">Cancel</b-button>
            <b-button variant="outline-danger" @click="deleteFilteredKeywordsCall">Yes</b-button>
          </div>
        </div>
      </template>
    </b-overlay>
  </div>
</template>
<script>
/* eslint-disable */
import { HTTP } from "../../utils/HTTP";
import moment from "moment";
import "moment/locale/es";
export default {
  name: "PriSettingsStopWordsPage",
  data() {
    return {
      responseSuccess: false,
      submiting: false,
      isLoading: false,
      showModal: false,
      tableFields: [
        { key: "created_on", label: "Age", sortable: true },
        { key: "keyword", label: "Keyword", sortable: true },
        { key: "name", label: "Created By", sortable: true },
        { key: "actions", label: "Actions", sortable: false }
      ],
      filter: null,
      perPage: 10,
      totalRows: 0,
      currentPage: 1,
      sortBy: "keyword_id",
      sortDesc: true,
      pageOptions: [5, 10, 25, 50, 75, 100],
      editFilteredKeywords: false,
      addStopWord: null,
      stopWordsList: [],
      fieldsKeywords: [
        { key: "text", label: "Keyword" },
        { key: "Actions", label: "Actions" }
      ],
      showEditModal: false,
      filterKeywordId: null,
      showDeleteFilteredKeywordsModal: false,
      processing: false,
      counter: 1,
      interval: null
    };
  },
  computed: {
    rows() {
      return this.totalRows;
    }
  },
  beforeDestroy() {
    this.clearInterval();
  },
  methods: {
    clearInterval() {
      if (this.interval) {
        clearInterval(this.interval);
        this.interval = null;
      }
    },
    age(value) {
      let localeData = moment.updateLocale("en", {
        relativeTime: {
          future: "in %s",
          past: "%s ago",
          s: "hello, a few seconds",
          ss: "%d seconds",
          m: "a minute",
          mm: "%d minutes",
          h: "an hour",
          hh: "%d hours",
          d: "a day",
          dd: "%d days",
          M: "a month",
          MM: "%d months",
          y: "a year",
          yy: "%d years"
        }
      });
      return moment(value).fromNow();
    },
    reloadTable() {
      this.$root.$emit("bv::refresh::table", "my-table");
    },
    async getFilteredKeywordsData() {
      let formData = {
        filter: this.filter,
        sort: this.sortBy + "|" + (this.sortDesc == true ? "desc" : "asc"),
        per_page: this.perPage,
        page: this.currentPage
      };

      let token = "Bearer " + localStorage.getItem("accessToken");
      this.isLoading = true;
      let result = HTTP("post", "v1/filtered-keywords-data", formData, {
        Authorization: token
      });

      return result
        .then(response => {
          const items = response.data.data;
          this.isLoading = false;
          this.totalRows = response.data.total;
          return items;
        })
        .catch(error => {
          this.isLoading = false;
          return [];
        });
    },
    addStopWords() {
      if (this.addStopWord) {
        this.stopWordsList.unshift(this.addStopWord);
      }
      this.addStopWord = null;
    },
    removeStopWords(data) {
      let stopWordsData = this.stopWordsList;
      let stopWordsIndex = stopWordsData.indexOf(data.item);
      stopWordsData.splice(stopWordsIndex, 1);
    },
    onEditFilteredKeywords(data) {
      this.showEditModal = true;
      this.editFilteredKeywords = true;
      this.addStopWord = data.item.keyword;
      this.filterKeywordId = data.item.keyword_id;
    },
    onDeleteFilteredKeywords(data) {
      this.filterKeywordId = data.item.keyword_id;
      this.showDeleteFilteredKeywordsModal = true;
    },
    resetModal() {
      this.editFilteredKeywords = false;
      this.addStopWord = null;
      this.stopWordsList = [];
      this.responseSuccess = false;
      this.submiting = false;
      this.isLoading = false;
      this.showModal = false;
      this.showEditModal = false;
      this.filterKeywordId = null;
    },
    checkValidation() {
      if (this.editFilteredKeywords) {
        if (this.addStopWord) {
          this.updateFilteredKeywords().then(this.reloadTable);
        } else {
          alert("Please Fill input box");
          return;
        }
      } else {
        if (this.stopWordsList.length == 0) {
          alert("Please Add Atleast one stop word");
          return;
        } else {
          this.saveFilteredKeywords().then(this.reloadTable);
        }
      }
    },
    async saveFilteredKeywords() {
      let formData = {
        filterKeywords: this.stopWordsList,
        userId: localStorage.getItem("userId")
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/create-filtered-keywords", formData, {
          Authorization: token
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        // console.log("------------erroror", err);
      }
    },
    async updateFilteredKeywords() {
      let formData = {
        keywordId: this.filterKeywordId,
        filterKeyword: this.addStopWord,
        userId: localStorage.getItem("userId")
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        this.submiting = true;
        this.isLoading = true;
        await HTTP("post", "v1/update-filtered-keywords", formData, {
          Authorization: token
        });
        this.submiting = false;
        this.isLoading = false;
        this.responseSuccess = true;
      } catch (err) {
        // console.log("------------erroror", err);
      }
    },
    deleteFilteredKeywordsCall() {
      this.counter = 1;
      this.processing = true;
      this.deleteFilteredKeywords().then(this.reloadTable);
      this.clearInterval();
      this.interval = setInterval(() => {
        if (this.counter < 20) {
          this.counter = this.counter + 1;
        } else {
          this.clearInterval();
          this.$nextTick(() => {
            this.showDeleteFilteredKeywordsModal = this.processing = false;
          });
        }
      }, 350);
    },
    async deleteFilteredKeywords() {
      let formData = {
        keywordId: this.filterKeywordId,
        userId: localStorage.getItem("userId")
      };
      try {
        let token = "Bearer " + localStorage.getItem("accessToken");
        await HTTP("post", "v1/delete-filtered-keywords", formData, {
          Authorization: token
        });
      } catch (err) {
        // console.log("------------erroror", err);
      }
    },
    resetDeleteModal() {
      this.filterKeywordId = null;
      this.showDeleteFilteredKeywordsModal = false;
    }
  }
};
</script>